<script setup lang="ts">

import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, computed, onMounted } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import Swal from 'sweetalert2'
import axios from 'axios'
import type { AxiosError } from 'axios'
import type { IErrorResponse } from '../../types/AoeTypes'

import { EventGuestCreate } from '../../services/EventService.ts'
import { AddressBookFind } from '../../services/AddressBookService.ts'
import type { GridAddressBookFind } from '../../types/AddressBookTypes'
import type { IAddress } from '../../types/CommonTypes'

import type { SortDescriptor } from '@progress/kendo-data-query'
import { orderBy } from '@progress/kendo-data-query';
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import { Grid, type GridSelectionChangeEvent, type GridSortChangeEvent } from '@progress/kendo-vue-grid';
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('FindEventContactsControl.vue')

const props = withDefaults(defineProps<{
  showAddGuest: boolean
  beeNumberAuth: number | null
  eventidtoken: string | null
  initialSearchText?: string
  initialSelectedHostId?: number
  initialSelectedHostType?: string
  allowMultipleSelection?: boolean
}>(), {
  allowMultipleSelection: true
})

const txtSearch = ref<string>('')
const findHostessType = ref<string>('')
const findHostessError = ref<boolean>(false)
const findHostessId = ref<number>(0)
const addressBookSearchComplete = ref<boolean>(false)
const addressBookLoader = ref<boolean>(false)
const addressBookSort = ref<SortDescriptor[]>([{ field: 'firstName', dir: 'asc' }])
const addressBookGridData = ref<GridAddressBookFind[]>([])
const selectedField = ref<string>('selected')
const addressBookSortedGridData = computed<GridAddressBookFind[]>(() => orderBy(addressBookGridData.value, addressBookSort.value));
const addressBookColumns = [
        {
          field: 'selected',
          width: 50,
          headerSelectionValue: false
        },
        {
          field: 'firstName',
          title: t('eventList.FirstName'),
          width: resolveColumnWidth('150px', t('eventList.FirstName'))
        },
        {
          field: 'lastName',
          title: t('eventList.LastName'),
          width: resolveColumnWidth('150px', t('eventList.LastName'))
        },
        {
          field: 'emailAddress',
          title: t('eventList.EmailAddress'),
          width: resolveColumnWidth('200px', t('eventList.EmailAddress')),
        },
        {
          field: 'primaryPhone.formattedNumber',
          title: t('eventList.PhoneNumber'),
          width: resolveColumnWidth('150px', t('eventList.PhoneNumber'))
        },
        {
          field: 'contactBeeNumber',
          hidden: true
        },
        {
          field: 'addressBookId',
          hidden: true
        }
      ]

const emit = defineEmits(["addressChange", "findHostess", "guestGridRefresh", "searchTextChange"]);

// Restore state when component is mounted
onMounted(() => {
  // Restore search text if provided
  if (props.initialSearchText) {
    txtSearch.value = props.initialSearchText
    // Automatically perform search to restore grid data
    searchHostHandler(new MouseEvent('click'))
  }

  // Restore selected host information if provided
  if (props.initialSelectedHostId && props.initialSelectedHostType) {
    findHostessId.value = props.initialSelectedHostId
    findHostessType.value = props.initialSelectedHostType
    emit('findHostess', { findHostessType: findHostessType.value, findHostessId: findHostessId.value })
  }
})

function searchHostHandler(event: MouseEvent)
{
  logger.debug('searchHostHandler')
  event.preventDefault()
  addressBookSearchComplete.value = true
  addressBookLoader.value = true;
  addressBookGridData.value = []

  // Emit search text to parent so it can be preserved
  emit('searchTextChange', txtSearch.value)
  AddressBookFind({ searchText: txtSearch.value }).then((addressBookResp) => {
    if (addressBookResp)
    {
      logger.debug('searchHostHandler', addressBookResp)
      for (const ab of addressBookResp.items) {
        const gridItem = ab as GridAddressBookFind
        // Mark item as selected if it matches the initially selected host
        if (props.initialSelectedHostId &&
            ((props.initialSelectedHostType === 'addressBook' && gridItem.addressBookId === props.initialSelectedHostId) ||
             (props.initialSelectedHostType === 'bee' && gridItem.contactBeeNumber === props.initialSelectedHostId))) {
          gridItem.selected = true
        }
        addressBookGridData.value.push(gridItem)
      }
      addressBookLoader.value = false;
    }
  })
}

function handleSearchKeydown(...args: unknown[]) {
  const event = args[0] as KeyboardEvent
  if (event.key === 'Enter') {
    event.preventDefault()
    // Create a mock MouseEvent to pass to searchHostHandler
    const mockEvent = new MouseEvent('click')
    searchHostHandler(mockEvent)
  }
}

function addressBookSortChangeHandler (e: GridSortChangeEvent) {
  addressBookLoader.value = true;
  if (e.sort[0] !== undefined) {
    addressBookSort.value = e.sort;
  }
  else
  {
    logger.debug('sortChangeHandler: e.sort[0] is undefined')
  }
  addressBookLoader.value = false;
}


function onSelectionChange (event : GridSelectionChangeEvent) {
  logger.debug('onSelectionChange event', event)

  // Handle selection based on allowMultipleSelection prop
  if (props.allowMultipleSelection === false) {
    // Single selection mode: deselect all others first
    event.target.dataItems.forEach((item: GridAddressBookFind) => {
      if (item !== event.dataItem) {
        item.selected = false
      }
    })
    // Toggle the clicked item
    event.dataItem.selected = !event.dataItem.selected
  } else {
    // Multiple selection mode: just toggle the selected item
    event.dataItem.selected = !event.dataItem.selected
  }

  // Get all selected items for address/hostess info
  const selectedItems = event.target.dataItems.filter((item: GridAddressBookFind) => item.selected)

  if (selectedItems.length === 0)
  {
    findHostessType.value = ''
    findHostessId.value = 0
    emit('findHostess', { findHostessType: findHostessType.value, findHostessId: findHostessId.value })
    return
  }

  // Use the last selected item for hostess/address info (backward compatibility)
  const lastSelected = selectedItems[selectedItems.length - 1]

  if (lastSelected.addressBookId != null)
  {
    findHostessType.value = 'addressBook'
    findHostessId.value = lastSelected.addressBookId
  }
  else
  {
    findHostessType.value = 'bee'
    findHostessId.value = lastSelected.contactBeeNumber
  }

  emit('findHostess', { findHostessType: findHostessType.value, findHostessId: findHostessId.value })

  let address = {
    addressId: 0,
    firstName: null,
    lastName: null,
    addressLine1: '',
    addressLine2: '',
    houseNumber: null,
    city: '',
    stateCode: '',
    postalCode: '',
    countryCode: ''
  } as IAddress

  if (lastSelected.address != undefined)
  {
    address = {
      addressId: lastSelected.address.addressId,
      firstName: lastSelected.address.firstName,
      lastName: lastSelected.address.lastName,
      addressLine1: lastSelected.address.addressLine1,
      addressLine2: lastSelected.address.addressLine2,
      houseNumber: lastSelected.address.houseNumber,
      city: lastSelected.address.city,
      stateCode: lastSelected.address.stateCode,
      postalCode: lastSelected.address.postalCode,
      countryCode: lastSelected.address.countryCode
    } as IAddress
  }

  emit('addressChange', address)
}

async function guestAdd(event: MouseEvent)
{
  logger.debug('guestAdd')
  event.preventDefault()

  // Get all selected items from the grid
  const selectedItems = addressBookGridData.value.filter((item: GridAddressBookFind) => item.selected)

  if(selectedItems.length === 0)
  {
    findHostessError.value = true
    return
  }

  // Add each selected guest and wait for all to complete
  if (props.beeNumberAuth != null && props.eventidtoken != null)
  {
    const guestPromises = []

    for (const selectedItem of selectedItems)
    {
      if (selectedItem.contactBeeNumber && selectedItem.contactBeeNumber > 0)
      {
        guestPromises.push(EventAddGuest(props.eventidtoken, 'G', selectedItem.contactBeeNumber, null, false))
      }
      else if (selectedItem.addressBookId && selectedItem.addressBookId > 0)
      {
        guestPromises.push(EventAddGuest(props.eventidtoken, 'G', null, selectedItem.addressBookId, false))
      }
    }

    // Wait for all guests to be added, then refresh once
    // Use Promise.allSettled to continue even if some guests fail (e.g., duplicates)
    try {
      const results = await Promise.allSettled(guestPromises)

      // Check if at least one guest was successfully added
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value !== null).length

      if (successCount > 0) {
        // Refresh the grid if at least one guest was added successfully
        emit('guestGridRefresh')
        addressBookGridData.value = []
        txtSearch.value = ''
        addressBookSearchComplete.value = false
      } else if (results.every(r => r.status === 'fulfilled' && r.value === null)) {
        // All guests were duplicates (handled by EventAddGuest)
        // Clear the selection but keep the search results
        addressBookGridData.value.forEach(item => item.selected = false)
      }
    } catch (error) {
      logger.error('Error adding guests:', error)
    }
  }
}

async function EventAddGuest(eventIdToken: string, role: string, guestBeeNumber: number | null, addressBookId: number | null, shouldRefresh: boolean = true)
{
  try {
    const eventGuest = await EventGuestCreate(
    {
      codeIdEventRole: role,
      eventIdToken: eventIdToken,
      guestBeeNumber: guestBeeNumber,
      addressBookId: addressBookId
    })

    if (eventGuest && shouldRefresh)
    {
      emit('guestGridRefresh')
      addressBookGridData.value = []
      txtSearch.value = ''
      addressBookSearchComplete.value = false
    }
    return eventGuest
  } catch (error) {
    // Check if this is a duplicate guest error
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError
      if (axiosError.response && axiosError.response.data) {
        const errorResponse = axiosError.response.data as IErrorResponse
        if (errorResponse.responseStatus?.errorCode === 'SfaEventContactsUniqueConstraintException') {
          // Show user-friendly duplicate guest message
          await Swal.fire({
            title: t('alerts.ValidationError'),
            text: t('eventManagement.DuplicateGuestError'),
            icon: 'warning',
          })
          return null
        }
      }
    }
    // Re-throw other errors to be handled by the caller
    throw error
  }
}

</script>

<template>
  <div>
    <div class="row mt-3">
      <div class="col-12 col-md-6">
        <KTextBox
          :placeholder="t('eventList.SearchHost')"
          v-model="txtSearch"
          :rounded="'large'"
          :theme-color="'primary'"
          @keydown="handleSearchKeydown"
        ></KTextBox>
      </div>
      <div class="col-12 col-md-6 mt-2 mt-md-0">
        <button class="btnSearch"
          @click="searchHostHandler($event)">
          {{ t('eventList.SearchButton') }}
        </button>
      </div>
    </div>
    <div v-if="addressBookSearchComplete" class="row mt-3">
      <div class="col-12 address-book-search-complete-div">
        <Grid
          ref="gridAddressBook"
          :style="{ height: '300px' }"
          :data-items="addressBookSortedGridData"
          :resizable="true"
          :sortable="true"
          :sort="addressBookSort"
          :pageable="false"
          class="addressBookGrid"
          :columns="addressBookColumns"
          :selected-field="selectedField"
          @sortchange="addressBookSortChangeHandler"
          @selectionchange="onSelectionChange"
          :loader="addressBookLoader"
        >
        </Grid>
      </div>
    </div>
    <div v-if="findHostessError && addressBookSearchComplete && props.showAddGuest" class="row mt-3">
      <div class="col-12 errorFindHost">
        {{ t('eventManagement.FindGuestError') }}
      </div>
    </div>
    <div v-if="addressBookSearchComplete && props.showAddGuest" class="row mt-3">
      <div class="col-12 buttonShowAddGuest">
        <button class="btnSearch"
          @click="guestAdd($event)">
          {{ t('eventManagement.AddGuestButton') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.buttonShowAddGuest
{
  text-align: right;
}
</style>
